import { useState } from "react";
import { Alert, Button, FormGroup, Input, Label } from "reactstrap";

import { unwrapPayload } from "../../utilities";
import { ExportStatesTable } from "./ExportStatesTable";

type QueuePreviewMode = 'toPrepare' | 'toSend';
type QueuePreviewModeInfo =
{
    label: string
    statuses: string[]
    description: string
}

const queuePreviewModes: Record<QueuePreviewMode, QueuePreviewModeInfo> =
{
    toPrepare:
    {
        label: 'Wymagają przeliczenia',
        statuses: ['Dirty'],
        description: 'Produkty oznaczone do ponownej oceny. System przeliczy dla nich aktualny payload Google Merchant.'
    },
    toSend:
    {
        label: 'Gotowe do wysłania',
        statuses: ['Prepared'],
        description: 'Produkty z przygotowanym payloadem, które czekają na wysłanie do Google Merchant.'
    }
};

export function PendingPreviewSection
(
    {
        searchExportStates
    }
    :
    {
        searchExportStates: (values: any) => Promise<any>;
    }
)
{
    const [mode, setMode] = useState<QueuePreviewMode>('toPrepare');
    const [limit, setLimit] = useState(25);
    const [result, setResult] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const loadPreview = async (nextMode: QueuePreviewMode = mode) =>
    {
        setError(null);
        setIsLoading(true);
        setMode(nextMode);

        try
        {
            const selectedMode = queuePreviewModes[nextMode];

            const response = await searchExportStates({
                statuses: selectedMode.statuses,
                limit
            });

            setResult(unwrapPayload(response));
        }
        catch (exception: any)
        {
            setError(exception?.message ?? 'Pobieranie kolejki procesu zakończyło się błędem.');
        }
        finally
        {
            setIsLoading(false);
        }
    };

    const selectedMode = queuePreviewModes[mode];

    return (
        <section className="mt-5">
            <h5>Podgląd stanów eksportu</h5>

            {error && <Alert color="danger">{error}</Alert>}

            <div className="row">
                <div className="col-12 col-xl-3">
                    <FormGroup>
                        <Label>Limit</Label>
                        <Input
                            type="number"
                            min={1}
                            max={250}
                            value={limit}
                            onChange={event => setLimit(Number.parseInt(event.target.value, 10) || 25)}
                        />
                    </FormGroup>
                </div>

                <div className="col-12 col-xl-9 d-flex align-items-end justify-content-xl-end">
                    <Button
                        type="button"
                        color={mode === 'toPrepare' ? 'primary' : 'secondary'}
                        className="mr-2 mb-3"
                        disabled={isLoading}
                        onClick={() => loadPreview('toPrepare')}
                    >
                        Pokaż do przygotowania
                    </Button>

                    <Button
                        type="button"
                        color={mode === 'toSend' ? 'primary' : 'secondary'}
                        className="mb-3"
                        disabled={isLoading}
                        onClick={() => loadPreview('toSend')}
                    >
                        Pokaż do wysłania
                    </Button>
                </div>
            </div>

            {result && (
                <div className="mt-3">
                    {result.message && (
                        <Alert color={result.success === false ? 'warning' : 'success'}>
                            {result.message}
                        </Alert>
                    )}

                    <ExportStatesTable states={result.states || []} />

                    <details className="mt-4">
                        <summary>Raw JSON</summary>
                        <pre className="mt-3">{JSON.stringify(result, null, 2)}</pre>
                    </details>
                </div>
            )}
        </section>
    );
}